// ABOUTME: REST helper for the generic node-assignment action.
// ABOUTME: Posts to /api/hierarchy/:hierarchyId/:nodeId/assign with the target and assignee node id.

export type AssignTarget = 'owner' | 'dealership' | 'client';

export type AssignmentPropagationNode = {
    hierarchyId: string;
    nodeId: string;
    code: string;
    listName?: string | null;
};

export type AssignmentPropagationGroup = {
    hierarchyId: string;
    hierarchyCode: string;
    nodes: AssignmentPropagationNode[];
};

export type AssignNodeResponse =
    | { choiceRequired?: false; success: boolean; data: Record<string, unknown> }
    // The object schema asks before propagating the assignment to linked nodes: nothing was
    // assigned, and the request must be re-sent with an explicit `propagate`.
    | { choiceRequired: true; linkedNodes: AssignmentPropagationGroup[] };

// Generic node assignment. The endpoint lives under /api/hierarchy; the caller passes the node's
// own hierarchy id.
export async function assignNode(
    hierarchyId: string,
    nodeId: string,
    target: AssignTarget,
    assigneeNodeId: string,
    propagate?: boolean,
): Promise<AssignNodeResponse> {
    const response = await fetch(
        `/api/hierarchy/${encodeURIComponent(hierarchyId)}/${encodeURIComponent(nodeId)}/assign`,
        {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ target, assigneeNodeId, propagate }),
        },
    );

    if (!response.ok) {
        const errorBody = await response.json().catch(() => ({ error: response.statusText }));
        if (response.status === 409 && errorBody.choiceRequired) {
            return { choiceRequired: true, linkedNodes: errorBody.linkedNodes ?? [] };
        }
        throw new Error(errorBody.error || `Assign API error: ${response.status}`);
    }

    return response.json();
}
